/**
 * P3-16: HuntBtn —— 狩猎按钮（迁移自 js/village.js:4688-4772）。
 *
 * 旧实现要点：
 * - 名称/描述：i18n `village.btn.hunters` / `village.btn.hunters.desc`；
 * - 价格：prices=[{manpower, 100 - huntCatpowerDiscount}]，由 VillageButtonController
 *   基类求 enabled（hasResources）；
 * - visible：archery 已研究且非 pacifism；
 * - links：all / half / fifth，visible 受 showNonApplicableButtons 或 squads>1 控制，
 *   title 随 usePercentageConsumptionValues 在百分比与 squad 数间切换；
 * - 主点击：buyItem(model, event)，支持 Shift=∞、Ctrl/Cmd=batchSize，
 *   上限为 getMaxHuntAmt()；link 点击直调 village.huntFraction(divisor)。
 */
import { useCallback, useMemo } from "react";
import type { MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { GameButton } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import { makeModernTooltip, useButtonModel } from "../bonfire/useButtonModel";
import type { ModernButtonController } from "../bonfire/useButtonModel";

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

interface HuntLinkSnap {
  id: string;
  title: string;
  tooltip: string;
  visible: boolean;
  handler: () => void;
}

interface HuntSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: boolean | string;
  links: HuntLinkSnap[];
}

interface HuntButtonControllerLike extends ModernButtonController {
  buyItem(model: unknown, event?: unknown): { itemBought: boolean; reason: string };
}

// ---------------------------------------------------------------------------
// 快照
// ---------------------------------------------------------------------------

function toHuntSnapshot(model: Record<string, unknown>): HuntSnapshot {
  const allLink = model.allLink as HuntLinkSnap | undefined;
  const halfLink = model.halfLink as HuntLinkSnap | undefined;
  const fifthLink = model.fifthLink as HuntLinkSnap | undefined;
  return {
    name: model.name as string,
    description: (model.description as string) ?? "",
    visible: model.visible !== false,
    enabled: model.enabled !== false,
    resourceIsLimited: (model.resourceIsLimited as boolean | string) ?? "",
    links: [allLink, halfLink, fifthLink].filter(Boolean).map((l) => l as HuntLinkSnap),
  };
}

function huntSnapshotEq(a: HuntSnapshot | null, b: HuntSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.name !== b.name ||
    a.description !== b.description ||
    a.visible !== b.visible ||
    a.enabled !== b.enabled ||
    a.resourceIsLimited !== b.resourceIsLimited ||
    a.links.length !== b.links.length
  ) {
    return false;
  }
  for (let i = 0; i < a.links.length; i++) {
    const la = a.links[i];
    const lb = b.links[i];
    if (la.id !== lb.id || la.title !== lb.title || la.tooltip !== lb.tooltip || la.visible !== lb.visible) {
      return false;
    }
  }
  return true;
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

export function HuntBtn() {
  const controller = useMemo(() => {
    const game = getGame();
    const classes = (window as unknown as Record<string, unknown>).classes as Record<string, unknown> | undefined;
    const village = classes?.village as Record<string, unknown> | undefined;
    const ui = village?.ui as Record<string, unknown> | undefined;
    const Ctor = ui?.HuntButtonController as new (game: GamePage) => HuntButtonControllerLike;
    if (!game || !Ctor) {
      return null;
    }
    return new Ctor(game);
  }, []);

  const createOpts = useCallback(
    (game: GamePage) => {
      const manpowerCost = 100 - (game.getEffect("huntCatpowerDiscount") as number);
      return {
        name: t("village.btn.hunters"),
        description: t("village.btn.hunters.desc"),
        prices: [{ name: "manpower", val: manpowerCost }],
      };
    },
    [],
  );

  const { snap, modelRef } = useButtonModel(controller, createOpts, toHuntSnapshot, huntSnapshotEq);

  const onClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(modelRef.current, event);
    },
    [controller, modelRef],
  );

  const getTooltip = useMemo(
    () => makeModernTooltip(() => controller, createOpts),
    [controller, createOpts],
  );

  const getSelectedObject = useCallback(() => modelRef.current, [modelRef]);

  if (!snap) {
    return null;
  }

  const linkModels: GameButtonLinkModel[] = snap.links.map((link) => ({
    id: link.id,
    title: link.title,
    tooltip: link.tooltip,
    visible: link.visible,
    handler: () => {
      link.handler();
    },
  }));

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      visible={snap.visible}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      links={linkModels}
      getTooltip={getTooltip}
      onClick={onClick}
      getSelectedObject={getSelectedObject}
    />
  );
}
